import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";
import {listAllNonConfirmedOrders} from "api/order/list";

// Material UI
import Grid from "@mui/material/Grid";
import {Radio, RadioGroup} from "@mui/material";

// Components
import FormControlLabel from "components/radioGroup/FormControlLabel";
import CustomButton from "components/CustomButton";
import BottomDrawer from "components/WMS/Mobile/BottomDrawer";

// Local components
import IsServiceWarning from "./components/IsServiceWarning";
import SpecialOrderItem from "./components/SpecialOrderItem";
import {getWmsItemsFromSpecialOrders} from "../../../../../../../../api/wms/wmsItems/wmsItems";
import SearchBar from "../../../../../../../../components/List/components/SearchBar";

SpecialOrderDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    currentInventory: PropTypes.object.isRequired,
    onClickSelectedOrder: PropTypes.func,
};

/*
 ReservedOrders are custom orders. They need a retailer make a custom change to one or more items.
 SpecialOrders are orders from non-WMS inventories that need to be handled inside a WMS inventory for shipping or CC, they drop inside the packing view with a WMSItems "special" status. Their lineItems are in to-ship.

 In non-development terms both type are considered "Special orders".
 */

function SpecialOrderDrawer({isOpen, onClose, currentInventory = {}, onClickSelectedOrder = (selectedSpecialOrder, shipmentIndex) => {}}) {
    const [reservedOrders, setReservedOrders] = useState([])
    const [specialOrders, setSpecialOrders] = useState([])
    const [shipmentIndex, setShipmentIndex] = useState(0)
    const [selectedShipment, setSelectedShipment] = useState("")
    const [searchText, setSearchText] = useState("")

    const searchTimer = useRef()

    const handleStart = (currentInventory) => {
        setSearchText("")

        listAllNonConfirmedOrders(`?groups[]=wms_packing:read&maxItems=30&inventoryId=${currentInventory.id}`).then((response) => {
            setReservedOrders(response)
        })

        getWmsItemsFromSpecialOrders(`?inventoryId=${currentInventory.id}&maxItemsNumber=${10}`).then((response) => {
            setSpecialOrders(formatWmsItems(response))
        })
    }

    const handleSearch = (searchText) => {
        clearTimeout(searchTimer.current)

        setSearchText(searchText)

        if (!searchText || "" === searchText) {
            handleStart(currentInventory)
            return;
        }

        searchTimer.current = setTimeout(() => {
            listAllNonConfirmedOrders(`?groups[]=wms_packing:read&maxItems=30&inventoryId=${currentInventory.id}&searchString=${searchText}`).then((response) => {
                setReservedOrders(response)
            })

            getWmsItemsFromSpecialOrders(`?inventoryId=${currentInventory.id}&searchString=${searchText}`).then((response) => {
                setSpecialOrders(formatWmsItems(response))
            })
        }, 500)
    }

    function getOrderBySelectedId(id) {
        return reservedOrders.find((order) => order.shipments.some(shipment => shipment.id === id)) ?? specialOrders.find(order => order.shipments.some(shipment => shipment.id === id));
    }

    function getLineItemsFromWmsItem (item) {
        const lineItem = item.lineItem
        return {
            id: lineItem.id,
            status: lineItem.status,
            variation: lineItem.variation.id,
            variationSku: lineItem.variationSku,
            pictures: lineItem.pictures,
            options: [],
            wmsItem: {
                id: item.id,
                status: item.status,
                barcode: item.barcode,
                createdAt: item.createdAt,
                updatedAt: item.updatedAt,
                inventory: item.inventory?.id
            }
        }
    }

    function formatWmsItems(wmsItemsList) {
        const specialOrderList = []

        wmsItemsList.map(item => {
            const itemOrder = item.lineItem.currentShipment.order
            if (!specialOrderList.some(order => order.id === itemOrder.id)) {
                itemOrder.shipments = []
                specialOrderList.push(itemOrder)
            }

            const itemShipment = item.lineItem.currentShipment

            const targetOrder = specialOrderList.find(order => order.id === itemOrder.id);
            if (targetOrder) {
                const existingShipment = targetOrder.shipments.find(s => s.id === itemShipment.id);
                if (!existingShipment) {
                    delete itemShipment.order;
                    itemShipment.lineItems = [getLineItemsFromWmsItem(item)];
                    targetOrder.shipments.push(itemShipment);
                } else {
                    existingShipment.lineItems.push(getLineItemsFromWmsItem(item));
                }
            }
        })

        return specialOrderList
    }

    useEffect(() => {
        handleStart(currentInventory)
    }, [currentInventory]);

    return (
        <BottomDrawer
            open={isOpen}
            onClose={onClose}
            openMaxHeight
            noHeightLimit
            disablePortal
            title={<FormattedMessage id={"wms.packing.specialOrder.title"}/>}
            subtitle={<FormattedMessage id={"wms.packing.specialOrder.subtitle"}/>}
            underTitle={
                <SearchBar
                    onChange={(value) => handleSearch(value)}
                />
            }
        >
            <Grid container>
                <Grid container direction={"column"} xs={12} sx={{paddingX: "8px"}}>
                    {reservedOrders.length === 0 && specialOrders.length === 0 && (
                        <div style={{padding: '16px'}}>
                            <FormattedMessage id={"wms.packing.specialOrder.noOrder"}/>
                        </div>
                    )}
                    <RadioGroup
                        onChange={(event) => setSelectedShipment(event.target.value)}
                        value={selectedShipment}
                    >
                        {reservedOrders.map((order) =>
                            order.shipments.map((shipment, index) => {
                                    return (
                                        <div key={shipment.id}>
                                            <FormControlLabel
                                                control={<Radio/>}
                                                key={shipment.id}
                                                value={shipment.id}
                                                onClick={() => setShipmentIndex(index)}
                                                label={<SpecialOrderItem order={order} shipmentIndex={index}/>}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    margin: 0,
                                                    padding: "16px",
                                                    border: 'unset',
                                                }}
                                            />
                                            <IsServiceWarning order={order}/>
                                        </div>)
                                }
                            )
                        )}

                        {specialOrders.map((order) =>
                            order.shipments.map((shipment, index) => {
                                    return (
                                        <div key={shipment.id}>
                                            <FormControlLabel
                                                control={<Radio/>}
                                                key={shipment.id}
                                                value={shipment.id}
                                                onClick={() => setShipmentIndex(index)}
                                                label={<SpecialOrderItem order={order} shipmentIndex={index}/>}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    margin: 0,
                                                    padding: "16px",
                                                    border: 'unset',
                                                }}
                                            />
                                            <IsServiceWarning order={order}/>
                                        </div>
                                    )
                                }
                            )
                        )}
                    </RadioGroup>
                </Grid>
            </Grid>

            {(reservedOrders.length > 0 || specialOrders.length > 0) && (
                <div style={{
                    background: '#fff',
                    padding: '16px',
                    position: 'sticky',
                    bottom: 0,
                }}>
                    <CustomButton
                        disabled={!selectedShipment}
                        fullWidth={true}
                        thicker={true}
                        onClick={() => {
                            onClose();
                            onClickSelectedOrder(getOrderBySelectedId(selectedShipment), shipmentIndex);
                        }}
                    >
                        <FormattedMessage id='wms.packing.specialOrder.action' />
                    </CustomButton>
                </div>
            )}
        </BottomDrawer>
    );
}

export default SpecialOrderDrawer;
